/**
 * Customer sign-in (identity contract): magic link request, `#token=` consume (fragment removed with
 * history.replaceState), then 2FA verify - or first-time 2FA enrolment for roles that require it (a new
 * self-serve owner lands here from the verification email). The email step and sign-up use Cloudflare Turnstile
 * when enabled (invisible unless Cloudflare asks; lib/turnstile.ts); the 2FA field is a labelled numeric
 * input with autocomplete="one-time-code" and errors announced via role="alert", so the flow is completable
 * with a screen reader (QM-NX-016 security scenario). Returns to ?next= after sign-in (QM-D19-002).
 * Rendered standalone (AuthLayout), outside the portal shell. Also served at /sign-in and /sign-in/link.
 * Passkeys (flag identity.passkeys; hidden while the API answers 404): "Sign in with a passkey" plus conditional
 * mediation on the email field, a passkey option at the 2FA step, and passkey setup as an alternative to TOTP.
 * Recovery: the 2FA step accepts a recovery code (POST /2fa/recover -> forced re-enrolment); a first passkey on an
 * account without codes returns a set, shown once like the TOTP ones.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, ErrorState, Field, Heading, Panel, Text, TextInput } from '@qm/ui';
import { surfaceForEmailDomain } from '@qm/contracts/identity';
import { adminOriginFor } from '@qm/tenancy/surface';
import { loadSession, useApp } from '../../../lib/app-context';
import { BffError, bff, bffJson } from '../../../lib/bff';
import { botCheckHeaders } from '../../../lib/turnstile';
import { isInstalledApp, keepSignedInWanted, setKeepSignedInWanted } from '../../../lib/keep-signed-in';
import { autofillSupported, passkeyErrorKey, passkeySecondFactor, passkeySignIn, passkeysSupported, registerPasskey, signInOptions } from '../../../lib/passkeys';
import { authDestination, rememberNext, safeNext, tokenFromHash } from '../../../lib/deeplink';

import { AuthenticatorSetup, SecurityChoice } from './SecuritySetup';
import { PhoneVerification } from '../setup/PhoneVerification';
import type { PhoneStatus } from '@qm/contracts/customer-setup';
export { tokenFromHash };

type Step = 'email' | 'sent' | 'verifying' | 'totp' | 'enrol' | 'recovery' | 'phone';

/** consume -> which step comes next ('mfa' is the API value; older values kept for compatibility). */
export function stepAfterConsume(next: string | undefined): 'totp' | 'enrol' | 'app' {
  if (next === 'mfa' || next === 'mfa_verify' || next === 'totp') return 'totp';
  if (next === 'enrol_mfa') return 'enrol';
  return 'app';
}

const numeric = (v: string) => v.replace(/[٠-٩۰-۹]/g, c => String(c.charCodeAt(0) - (c <= '٩' ? 0x660 : 0x6f0))).replace(/\D/g, '');

/** Which second-factor controls the 2FA step shows (consume returns mfaMethods; absent = TOTP only). */
export function secondFactorControls(methods: string[] | undefined): { code: boolean; passkey: boolean } {
  const m = methods?.length ? methods : ['totp'];
  return { code: m.includes('totp'), passkey: m.includes('passkey') };
}

export function SignInPage() {
  const { t, locale } = useApp();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const nextQuery = params.get('next') ? `?next=${encodeURIComponent(next)}` : '';
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [factor, setFactor] = useState<{ factorId: string; secret: string; otpauthUri: string } | null>(null);
  const [recovery, setRecovery] = useState<string[]>([]);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoverySaved, setRecoverySaved] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const consumed = useRef(false);
  const [error, setError] = useState<string | null>(null);
  // Owner request 27 Sep 2026: a @quixohub.com / @quixomarketing.com email typed here belongs on the staff
  // sign-in. Domain-only, client-side, no account lookup - see @qm/contracts/identity surfaceForEmailDomain.
  // G14: the owner tests the customer flow with a staff address, so "Continue as a customer" keeps the magic link here
  const [asCustomer, setAsCustomer] = useState(false);
  const isStaffEmail = surfaceForEmailDomain(email) === 'admin' && !asCustomer;
  const staffSigninUrl = typeof window === 'undefined' ? '/signin' : `${adminOriginFor(window.location.host, window.location.protocol)}/signin`;
  const staffMicrosoftUrl = typeof window === 'undefined' ? '/signin' : `${adminOriginFor(window.location.host, window.location.protocol)}/bff/v1/identity/entra/start`;
  const [passkeyOn, setPasskeyOn] = useState(false);
  const installedApp = isInstalledApp();
  const [keepSignedIn, setKeepSignedIn] = useState(() => keepSignedInWanted());
  const [methods, setMethods] = useState<string[] | undefined>(undefined);
  const controls = secondFactorControls(methods);
  const finish = () => window.location.assign(authDestination(next));

  const startEnrol = async (phoneChecked = false) => {
    setFactor(null); setCode('');
    if (passkeysSupported()) void bff('/v1/identity/passkeys').then(() => setPasskeyOn(true), () => undefined);
    if (!phoneChecked) {
      try { const p = await bff<PhoneStatus>('/v1/identity/phone/status'); if (p.required && !p.verified) { setStep('phone'); return; } }
      catch { setError(t('shell.error.action')); setStep('email'); return; }
    }
    setStep('enrol');
  };
  const beginAuthenticator = async () => {
    if (enrolling) return;
    setEnrolling(true); setError(null);
    try { setFactor(await bffJson<{factorId:string;secret:string;otpauthUri:string}>('/v1/identity/2fa/totp/enrol','POST',{})); }
    catch { setError(t('shell.error.action')); }
    finally { setEnrolling(false); }
  };
  useEffect(() => { if (params.get('next')) rememberNext(params.get('next')); }, [params]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (consumed.current) return;
    const token = tokenFromHash(window.location.hash);
    if (token) {
      if (consumed.current) return;
      consumed.current = true;
      history.replaceState(null, '', window.location.pathname + window.location.search);
      setStep('verifying');
      bffJson<{ next?: string; mfaMethods?: string[] }>('/v1/identity/magic-links/consume', 'POST', { token }).then(
        (r) => {
          setMethods(r.mfaMethods);
          const s = stepAfterConsume(r.next);
          if (s === 'totp') setStep('totp');
          else if (s === 'enrol') void startEnrol();
          else finish();
        },
        () => { setError(t('shell.signin.linkInvalid')); setStep('email'); },
      );
      return;
    }
    // Passkey sign-in: offered only when the API answers (flag on); autofill waits for a pick from the email field.
    let live = true;
    void signInOptions().then(async (options) => {
      if (!options || !live) return;
      setPasskeyOn(true);
      if (await autofillSupported()) passkeySignIn({ options, autofill: true }).then(finish, () => undefined);
    });
    // Already signed in (deep link re-auth): resume ?next= without asking for the email again.
    if (params.get('next')) loadSession().then((s) => { if (!live || !s) return; if (s.me.mfa.state === 'enrol_required') void startEnrol(); else window.location.assign(authDestination(next)); });
    return () => { live = false; };
    // runs once per page load: the token is single use
  }, []);

  const request = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    // Staff accounts have no magic link: a @quixohub.com / @quixomarketing.com email goes to Microsoft sign-in on the admin site.
    if (isStaffEmail) { window.location.assign(staffMicrosoftUrl); return; }
    try {
      await bff('/v1/identity/magic-links', { method: 'POST', headers: await botCheckHeaders(e.currentTarget as HTMLElement, locale), body: JSON.stringify({ email, locale }) });
      setStep('sent');
    } catch (err) { setError(err instanceof BffError && err.status === 403 ? t('shell.error.botCheck') : t('shell.error.action')); }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    try { await bffJson('/v1/identity/2fa/verify', 'POST', { code }); window.location.assign(authDestination(next)); } catch { setError(t('shell.signin.codeInvalid')); }
  };
  const withPasskey = (run: () => Promise<unknown>, after: () => void = finish) => async () => {
    setError(null);
    try { await run(); after(); } catch (err) { setError(t(passkeyErrorKey(err))); }
  };
  const signInWithPasskey = withPasskey(() => passkeySignIn());
  const verifyWithPasskey = withPasskey(() => passkeySecondFactor());
  // A passkey-only account receives recovery codes once with its first passkey: show them before continuing.
  const enrolPasskey = async () => {
    if (enrolling) return;
    setEnrolling(true); setError(null);
    try {
      const r = await registerPasskey(t('shell.passkeys.defaultName'));
      if (r.recoveryCodes?.length) { setRecovery(r.recoveryCodes); setFactor(null); setStep('recovery'); } else finish();
    } catch (err) { setError(t(passkeyErrorKey(err))); } finally { setEnrolling(false); }
  };
  // Lost device: a recovery code opens the session once, disables every factor and forces re-enrolment.
  const recover = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    try { await bffJson('/v1/identity/2fa/recover', 'POST', { code: recoveryCode.trim() }); setRecoveryCode(''); void startEnrol(); } catch { setError(t('shell.signin.codeInvalid')); }
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    if (!factor) return;
    try {
      const r = await bffJson<{ recoveryCodes: string[] }>('/v1/identity/2fa/totp/confirm', 'POST', { factorId: factor.factorId, code });
      setRecovery(r.recoveryCodes); setFactor(null); setStep('recovery');
    } catch { setError(t('shell.signin.codeInvalid')); }
  };

  const codeField = (
    <Field label={t('shell.signin.code')} hint={step === 'totp' ? t('shell.signin.codeHint') : undefined} error={error}>
      {({ id, describedBy, invalid }) => <TextInput id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(e) => setCode(numeric(e.target.value))} />}
    </Field>
  );

  return (
    <section className="qm-page" aria-labelledby="page-title" data-testid="signin-page">
      <Panel>
        <Heading level={1} id="page-title">{step === 'enrol' ? t('shell.signin.enrolTitle') : step === 'recovery' ? t('shell.signin.recoveryTitle') : t('shell.signin.title')}</Heading>
        {step === 'email' ? (
          <>
            <Text>{t('shell.signin.subtitle')}</Text>
            <form onSubmit={request} noValidate>
              <Field label={t('shell.signin.email')} error={error}>
                {({ id, describedBy, invalid }) => <TextInput id={id} type="email" autoComplete={passkeyOn ? 'username webauthn' : 'email'} required value={email} aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(e) => setEmail(e.target.value)} />}
              </Field>
              {installedApp ? (
                <label className="qm-keep-signed-in" data-testid="keep-signed-in">
                  <input type="checkbox" checked={keepSignedIn} onChange={(e) => { setKeepSignedIn(e.target.checked); setKeepSignedInWanted(e.target.checked); }} />
                  {' '}{t('shell.signin.keepSignedIn')}
                </label>
              ) : null}
              <Button variant="primary" type="submit">{t('shell.signin.send')}</Button>
            </form>
            {passkeyOn ? <Button type="button" onClick={() => void signInWithPasskey()} data-testid="passkey-signin">{t('shell.signin.passkey')}</Button> : null}
            <Text>{t('shell.signin.noAccount')} <Link to={`/signup${nextQuery}`} data-testid="to-signup">{t('shell.signin.toSignup')}</Link></Text>
            {isStaffEmail ? (
              <Text tone="muted" data-testid="staff-hint">{t('shell.signin.staffHint')} <a href={staffSigninUrl} data-testid="staff-signin-link">{t('shell.signin.staffLink')}</a>{' '}
                <button type="button" className="qm-link-button" data-testid="continue-as-customer" onClick={() => setAsCustomer(true)}>{t('shell.p1.signin.asCustomer')}</button>
              </Text>
            ) : asCustomer ? <Text tone="muted" role="status">{t('shell.p1.signin.asCustomerNote')}</Text> : null}
          </>
        ) : step === 'sent' ? (
          <Text role="status">{t('shell.signin.sent')}</Text>
        ) : step === 'verifying' ? (
          <Text role="status">{t('common.loading')}</Text>
        ) : step === 'totp' ? (
          <>
            {controls.code ? (
              <form onSubmit={verify} noValidate>
                {codeField}
                <Button variant="primary" type="submit">{t('shell.signin.verify')}</Button>
              </form>
            ) : <Text>{t('shell.signin.passkeyPrompt')}</Text>}
            {controls.passkey ? <Button variant={controls.code ? 'secondary' : 'primary'} type="button" onClick={() => void verifyWithPasskey()} data-testid="passkey-2fa">{t('shell.signin.passkeyVerify')}</Button> : null}
            {!controls.code && error ? <ErrorState message={error} /> : null}
            {showRecovery ? (
              <form onSubmit={recover} noValidate data-testid="recovery-form" id="recovery-form">
                <Field label={t('shell.signin.recoveryCode')}>
                  {({ id, describedBy }) => <TextInput id={id} autoComplete="off" spellCheck={false} dir="ltr" maxLength={32} required autoFocus value={recoveryCode} aria-describedby={describedBy} onChange={(e) => setRecoveryCode(e.target.value)} />}
                </Field>
                <Button type="submit" disabled={recoveryCode.trim().length < 8}>{t('shell.signin.recoveryUse')}</Button>
              </form>
            ) : (
              <Button type="button" variant="secondary" aria-expanded={false} aria-controls="recovery-form" onClick={() => setShowRecovery(true)} data-testid="recovery-toggle">{t('shell.signin.recoveryToggle')}</Button>
            )}
          </>
        ) : step === 'phone' ? (
          <PhoneVerification onVerified={() => void startEnrol(true)} />
        ) : step === 'enrol' ? (
          <>
            {!factor ? <SecurityChoice passkeyAvailable={passkeyOn} onAuthenticator={() => void beginAuthenticator()} onPasskey={() => void enrolPasskey()} busy={enrolling} /> : <>
              <AuthenticatorSetup factor={factor} />
              <form onSubmit={confirm} noValidate>{codeField}<Button variant="primary" type="submit">{t('shell.signin.enrolConfirm')}</Button></form>
              <Button type="button" onClick={() => { setFactor(null); setCode(''); setError(null); }}>{t('shell.p1.signin.otherMethod')}</Button>
            </>}
            {error ? <ErrorState message={error} /> : null}
          </>        ) : (
          <>
            <Text role="status">{t('shell.signin.recoveryIntro')}</Text>
            <ul className="qm-auth__codes" dir="ltr">{recovery.map((rc) => <li key={rc}>{rc}</li>)}</ul>
            <label className="qm-setup__check"><input type="checkbox" checked={recoverySaved} onChange={e => setRecoverySaved(e.target.checked)} />{t('shell.p1.signin.recoverySaved')}</label>
            <Button variant="primary" type="button" disabled={!recoverySaved} onClick={() => window.location.assign(authDestination(next))}>{t('shell.signin.continue')}</Button>
          </>
        )}
        {error && step !== 'email' && step !== 'totp' && step !== 'enrol' ? <ErrorState message={error} /> : null}
      </Panel>
    </section>
  );
}
